import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Receipt,
  DownloadSimple,
  Printer,
  Buildings,
  ArrowsClockwise,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtARS = (n) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(n || 0);
const fmtNum = (n) => (n || 0).toLocaleString('es-AR');

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const InvoicingPanel = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [includeAll, setIncludeAll] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/invoicing`, {
        params: { year, month, include_all_statuses: includeAll },
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      toast.error('Error al cargar facturación');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, includeAll]);

  const downloadCsv = () => {
    const url = `${API_URL}/api/admin/invoicing/export.csv?year=${year}&month=${month}&include_all_statuses=${includeAll}`;
    window.open(url, '_blank');
  };

  const printInvoice = () => {
    window.print();
  };

  const yearOptions = useMemo(() => {
    const y = [];
    for (let i = now.getFullYear(); i >= now.getFullYear() - 5; i -= 1) y.push(i);
    return y;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground" data-testid="invoicing-loading">
        <ArrowsClockwise size={28} className="mx-auto animate-spin text-primary mb-3" />
        Cargando facturación...
      </div>
    );
  }

  const biz = data.business;
  const totals = data.totals;
  const period = data.period;

  return (
    <div className="space-y-5" data-testid="invoicing-panel">
      {/* Controls — se ocultan al imprimir */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Receipt size={22} weight="duotone" className="text-primary" /> Facturación mensual
          </h2>
          <p className="text-sm text-muted-foreground">
            Comprobante detallado listo para imprimir o exportar.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Mes</Label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="h-9 px-3 rounded-md bg-background border border-input text-sm"
              data-testid="invoicing-month"
            >
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Año</Label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="h-9 px-3 rounded-md bg-background border border-input text-sm"
              data-testid="invoicing-year"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs h-9 px-2 rounded border border-input">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              data-testid="invoicing-include-all"
            />
            Incluir todos los estados
          </label>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="invoicing-refresh">
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button size="sm" onClick={downloadCsv} data-testid="invoicing-export-csv">
            <DownloadSimple size={14} className="mr-1.5" /> CSV
          </Button>
          <Button size="sm" variant="outline" onClick={printInvoice} data-testid="invoicing-print">
            <Printer size={14} className="mr-1.5" /> Imprimir / PDF
          </Button>
        </div>
      </div>

      {/* ============ HOJA DE COMPROBANTE — visible en pantalla e imprimible ============ */}
      <div
        id="invoice-sheet"
        className="bg-white text-black rounded-lg shadow-lg border border-border p-8 sm:p-12 print:shadow-none print:border-0 print:rounded-none print:p-0"
        data-testid="invoicing-sheet"
      >
        {/* Header */}
        <header className="flex items-start justify-between flex-wrap gap-4 pb-6 border-b-2 border-gray-800">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center">
              <Buildings size={28} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-2xl font-black tracking-tight">{biz.business_name}</div>
              <div className="text-sm text-gray-700">{biz.website}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-gray-600">Comprobante de operaciones</div>
            <div className="text-lg font-bold">Nº {data.invoice_number}</div>
            <div className="text-xs text-gray-600">
              Período: {MONTHS[period.month - 1]} {period.year}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              Emitido: {new Date(data.generated_at).toLocaleString('es-AR')}
            </div>
          </div>
        </header>

        {/* Titular */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-sm">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-widest text-gray-600 mb-1">Titular</div>
            <div className="font-bold text-base">{biz.name}</div>
            <div>DNI: <span className="font-semibold">{biz.dni}</span></div>
            <div>{biz.address}</div>
            <div>{biz.locality}, {biz.province}</div>
            <div>{biz.country}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-widest text-gray-600 mb-1">Actividad</div>
            <div>Comercialización de servicios de marketing digital.</div>
            <div>Modalidad: pago por transferencia bancaria / MercadoPago.</div>
            <div>Moneda: Peso Argentino (ARS).</div>
          </div>
        </section>

        {/* Totales */}
        <section className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-4 rounded-lg bg-gray-100" data-testid="totals-orders">
            <div className="text-[10px] uppercase text-gray-600 tracking-wider">Operaciones</div>
            <div className="text-2xl font-bold tabular-nums">{fmtNum(totals.orders)}</div>
          </div>
          <div className="p-4 rounded-lg bg-emerald-100" data-testid="totals-amount">
            <div className="text-[10px] uppercase text-emerald-800 tracking-wider">Ingresos brutos</div>
            <div className="text-2xl font-bold tabular-nums text-emerald-900">{fmtARS(totals.amount)}</div>
          </div>
          <div className="p-4 rounded-lg bg-blue-100" data-testid="totals-avg">
            <div className="text-[10px] uppercase text-blue-800 tracking-wider">Ticket promedio</div>
            <div className="text-2xl font-bold tabular-nums text-blue-900">{fmtARS(totals.average_ticket)}</div>
          </div>
          <div className="p-4 rounded-lg bg-gray-100" data-testid="totals-currency">
            <div className="text-[10px] uppercase text-gray-600 tracking-wider">Moneda</div>
            <div className="text-2xl font-bold tabular-nums">{totals.currency}</div>
          </div>
        </section>

        {/* Por plataforma / servicio */}
        <section className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-600 mb-2">Ingresos por plataforma</h3>
            <table className="w-full text-sm">
              <tbody>
                {data.by_platform.length === 0 && (
                  <tr><td className="py-1 text-gray-500">Sin operaciones en este período.</td></tr>
                )}
                {data.by_platform.map((p) => (
                  <tr key={p.platform} className="border-b border-gray-200">
                    <td className="py-1.5">{p.platform}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">{fmtARS(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-600 mb-2">Servicios más vendidos</h3>
            <table className="w-full text-sm">
              <tbody>
                {data.by_service.length === 0 && (
                  <tr><td className="py-1 text-gray-500">Sin operaciones en este período.</td></tr>
                )}
                {data.by_service.slice(0, 8).map((s) => (
                  <tr key={s.service} className="border-b border-gray-200">
                    <td className="py-1.5">{s.service}</td>
                    <td className="py-1.5 text-right text-gray-600">{fmtNum(s.count)}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold">{fmtARS(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Detalle de operaciones */}
        <section className="mt-8">
          <h3 className="text-xs uppercase tracking-widest text-gray-600 mb-2">Detalle de operaciones</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b-2 border-gray-800 text-gray-700 uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 pr-2">Fecha</th>
                  <th className="text-left py-2 pr-2">Nº Op.</th>
                  <th className="text-left py-2 pr-2">Cliente</th>
                  <th className="text-left py-2 pr-2">Servicio</th>
                  <th className="text-right py-2 pr-2">Cantidad</th>
                  <th className="text-right py-2 pr-2">Importe</th>
                  <th className="text-left py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-6 text-gray-500">
                      Sin operaciones en este período con los filtros aplicados.
                    </td>
                  </tr>
                )}
                {data.orders.map((o) => (
                  <tr key={o.id} className="border-b border-gray-200" data-testid={`invoice-row-${o.id}`}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{(o.created_at || '').slice(0, 10)}</td>
                    <td className="py-1.5 pr-2 font-mono">{(o.id || '').slice(0, 8)}</td>
                    <td className="py-1.5 pr-2 truncate max-w-[180px]">{o.user_name || o.guest_name || '—'}</td>
                    <td className="py-1.5 pr-2 truncate max-w-[220px]">{o.service_name} · {o.platform}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{fmtNum(o.quantity)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">{fmtARS(o.price)}</td>
                    <td className="py-1.5">
                      <Badge variant="outline" className="text-[10px]">{o.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-800">
                <tr>
                  <td colSpan={5} className="py-2 pr-2 text-right font-bold uppercase tracking-wider text-xs">
                    Total del período
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums font-bold text-base">
                    {fmtARS(totals.amount)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-10 pt-4 border-t border-gray-300 text-[10px] text-gray-600 text-center leading-snug">
          Este comprobante refleja las operaciones registradas en el sistema propio de {biz.business_name}.
          Emitido de forma automática. No sustituye documentación fiscal oficial (AFIP / ARCA).
        </footer>
      </div>

      {/* Reglas de impresión para que el PDF salga limpio */}
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { background: #fff !important; color: #000 !important; }
          [data-testid="admin-sidebar"] { display: none !important; }
          .print\\:hidden { display: none !important; }
          #invoice-sheet { box-shadow: none !important; border: 0 !important; padding: 0 !important; background: #fff !important; }
        }
      `}</style>
    </div>
  );
};
